import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { uploadImage } from "@/services/storage";
import { useCollection } from "@/hooks/useCollection";
import { TradePicker } from "./ProCategoriesTab";

const pad = (n) => String(n).padStart(2, "0");
const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };

// Add / edit form. onSubmit(data) — caller decides whether a password gate is needed.
export function CategoryDialog({ open, initial, onClose, onSubmit }) {
  const [f, setF] = useState({});
  const [uploading, setUploading] = useState(false);
  const procats = useCollection("professional_categories");
  useEffect(() => {
    if (open) setF({ name: initial?.name || "", description: initial?.description || "", image_url: initial?.image_url || "", subcategories: (initial?.subcategories || []).join(", "), display_order: initial?.display_order ?? "", scheduled_end_at: toLocal(initial?.scheduled_end_at), pro_category_ids: (initial?.pro_category_ids || []).map(String) });
  }, [open, initial]);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(`app_content/categories/${Date.now()}_${file.name}`, file); setF((x) => ({ ...x, image_url: url })); toast.success("Image uploaded"); }
    catch (e) { toast.error(e?.code === "storage/unauthorized" ? "Storage denied — admin role required" : e.message); }
    finally { setUploading(false); }
  };
  const submit = () => {
    if (!f.name.trim()) return toast.error("Name required");
    const end = f.scheduled_end_at ? new Date(f.scheduled_end_at) : null;
    if (end && end <= new Date()) return toast.error("Scheduled end must be in the future");
    onSubmit({
      name: f.name.trim(), description: f.description || "", image_url: f.image_url || "",
      subcategories: f.subcategories.split(",").map((s) => s.trim()).filter(Boolean),
      display_order: f.display_order === "" ? null : Number(f.display_order),
      scheduled_end_at: end ? end.toISOString() : null,
      pro_category_ids: f.pro_category_ids || [],
    });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-category-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Edit" : "Add"} Service Category</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex items-center gap-4">
            <div className="h-20 w-20 rounded-lg border border-border bg-secondary overflow-hidden flex items-center justify-center shrink-0">
              {f.image_url ? <img src={f.image_url} alt="" className="w-full h-full object-cover" data-testid="category-image-preview" /> : <Icons.Image className="h-6 w-6 text-muted-foreground" />}
            </div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer rounded-lg border border-border px-3 py-2 hover:bg-accent" data-testid="category-image-label">
              <Icons.Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload cover"}
              <input type="file" accept="image/*" className="hidden" data-testid="category-image" onChange={(e) => upload(e.target.files[0])} />
            </label>
          </div>
          <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="category-name" value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. AC Service" /></div>
          <div className="space-y-1.5"><Label>Short description</Label><Textarea data-testid="category-desc" value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Subcategories / skills (comma separated)</Label><Textarea data-testid="category-subcats" rows={2} value={f.subcategories || ""} onChange={(e) => setF({ ...f, subcategories: e.target.value })} placeholder="AC installation, Gas refill, AC servicing" /></div>
          <div className="space-y-1.5 rounded-lg border border-border p-3">
            <Label>Professionals who do this <span className="font-normal text-muted-foreground">(one or more)</span></Label>
            <TradePicker value={f.pro_category_ids || []} onChange={(v) => setF({ ...f, pro_category_ids: v })} procats={procats} testId="category-trades" />
            <p className="text-[11px] text-muted-foreground">Requests for this service are floated to pros in every category picked here. Manage the list under Professional categories, below the services table.</p>
            {!(f.pro_category_ids || []).length && <p className="text-[11px] text-red-700 flex items-center gap-1"><Icons.AlertTriangle className="h-3.5 w-3.5" />Pick at least one — without it this service is left off brugo.in.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Display order</Label><Input type="number" data-testid="category-order" value={f.display_order ?? ""} onChange={(e) => setF({ ...f, display_order: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Scheduled end (optional)</Label><Input type="datetime-local" data-testid="category-end" value={f.scheduled_end_at || ""} onChange={(e) => setF({ ...f, scheduled_end_at: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="save-category">{initial?.id ? "Save" : "Continue"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
